/**
 * StatusCell Component
 *
 * Renders a status column value with a color-coded label.
 * Clicking opens a dropdown to select from available status labels.
 */

import { useState, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/24/outline';
import type { StatusCellProps } from './types';
import { DEFAULT_STATUS_LABELS } from '@wmp/shared/constants';

export function StatusCell({
  value,
  onChange,
  labels,
  readonly = false,
}: StatusCellProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Use provided labels or defaults
  const statusLabels = labels ?? [...DEFAULT_STATUS_LABELS];

  // Find the current label
  const currentLabel = value ? statusLabels.find(l => l.id === value.labelId) : null;

  const handleSelect = (labelId: string) => {
    if (readonly || !onChange) return;

    if (labelId === '') {
      // Clear selection
      onChange(null);
    } else {
      const selected = statusLabels.find(l => l.id === labelId);
      if (selected) {
        onChange({
          labelId: selected.id,
          label: selected.label,
          color: selected.color,
        });
      }
    }
    setIsOpen(false);
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {currentLabel ? (
          <span
            className="status-label"
            style={{ backgroundColor: currentLabel.color }}
          >
            {currentLabel.label}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <Listbox value={value?.labelId ?? ''} onChange={handleSelect}>
      <div className="relative w-full">
        <Listbox.Button
          className="w-full h-full flex items-center cursor-pointer min-h-[28px]"
          onClick={() => setIsOpen(true)}
        >
          {currentLabel ? (
            <span
              className="status-label"
              style={{ backgroundColor: currentLabel.color }}
            >
              {currentLabel.label}
            </span>
          ) : (
            <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
          )}
        </Listbox.Button>

        <Transition
          show={isOpen}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setIsOpen(false)}
        >
          <Listbox.Options
            static
            className="absolute z-50 mt-1 w-44 bg-white rounded-lg shadow-dropdown border border-surface-border py-1 max-h-60 overflow-auto focus:outline-none"
          >
            {/* Clear option */}
            <Listbox.Option
              value=""
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 px-3 ${
                  active ? 'bg-surface-hover' : ''
                }`
              }
            >
              {({ selected }) => (
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded bg-gray-200" />
                  <span className="text-sm text-gray-500">Clear</span>
                  {selected && <CheckIcon className="h-4 w-4 ml-auto text-primary-500" />}
                </div>
              )}
            </Listbox.Option>

            <div className="border-t border-surface-border my-1" />

            {statusLabels.map((label) => (
              <Listbox.Option
                key={label.id}
                value={label.id}
                className={({ active }) =>
                  `relative cursor-pointer select-none py-2 px-3 ${
                    active ? 'bg-surface-hover' : ''
                  }`
                }
              >
                {({ selected }) => (
                  <div className="flex items-center gap-2">
                    <span
                      className="status-label text-xs"
                      style={{ backgroundColor: label.color }}
                    >
                      {label.label}
                    </span>
                    {selected && <CheckIcon className="h-4 w-4 ml-auto text-primary-500" />}
                  </div>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  );
}
